import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/lib/auth";
import { FeatureFlagsProvider } from "@/lib/featureFlags";
import ProtectedRoute from "@/components/ProtectedRoute";
import AppShell from "@/components/AppShell";
import BugReportOverlay from "@/components/dev/BugReportOverlay";
import "@/App.css";

// Pages (lazy pour split un peu de bundle)
const Landing = lazy(() => import("@/pages/Landing"));
const ProfileTemplatePage = lazy(() => import("@/pages/ProfileTemplatePage"));
const Login = lazy(() => import("@/pages/Login"));
const Register = lazy(() => import("@/pages/Register"));
const AuthCallback = lazy(() => import("@/pages/AuthCallback"));
const Onboarding = lazy(() => import("@/pages/Onboarding"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const OnboardingPage = lazy(() => import("@/pages/OnboardingPage"));
const DossierPage = lazy(() => import("@/pages/DossierPage"));
const AltioraPage = lazy(() => import("@/pages/AltioraPage"));
const FeatureFlagsAdmin = lazy(() => import("@/pages/admin/FeatureFlagsAdmin"));
const Assistant = lazy(() => import("@/pages/Assistant"));
const DocumentsPage = lazy(() => import("@/pages/DocumentsPage"));
const GenerationPage = lazy(() => import("@/pages/GenerationPage"));
const GeolocationPage = lazy(() => import("@/pages/GeolocationPage"));
const InstitutionPage = lazy(() => import("@/pages/InstitutionPage"));
const PartnersPage = lazy(() => import("@/pages/PartnersPage"));
const PartnerLandingPage = lazy(() => import("@/pages/PartnerLandingPage"));
const PartnerHubPage = lazy(() => import("@/pages/PartnerHubPage"));
const FolioMapInstitutionPage = lazy(() => import("@/pages/FolioMapInstitutionPage"));
const TimelinePage = lazy(() => import("@/pages/TimelinePage"));
const PlansPage = lazy(() => import("@/pages/PlansPage"));
const AgendaPage = lazy(() => import("@/pages/AgendaPage"));
const CopilotPage = lazy(() => import("@/pages/CopilotPage"));
const OpportunitiesPage = lazy(() => import("@/pages/OpportunitiesPage"));
const SecurityCenterPage = lazy(() => import("@/pages/SecurityCenterPage"));
const KnowledgeCenterPage = lazy(() => import("@/pages/KnowledgeCenterPage"));
const PublicPortalPage = lazy(() => import("@/pages/PublicPortalPage"));
const FieldPage = lazy(() => import("@/pages/FieldPage"));
const CollectionPage = lazy(() => import("@/pages/CollectionPage"));
const OutputEnginePage = lazy(() => import("@/pages/OutputEnginePage"));
const ProgressionPage = lazy(() => import("@/pages/ProgressionPage"));
const ProjectsPage = lazy(() => import("@/pages/ProjectsPage"));
const InstitutionPreview = lazy(() => import("@/pages/projects/InstitutionPreview"));
const OnboardingWizard = lazy(() => import("@/pages/OnboardingWizard"));
const SharedView = lazy(() => import("@/pages/SharedView"));
const PaymentSuccessPage = lazy(() =>
  import("@/pages/PaymentResultPages").then((m) => ({ default: m.PaymentSuccessPage }))
);
const PaymentCancelPage = lazy(() =>
  import("@/pages/PaymentResultPages").then((m) => ({ default: m.PaymentCancelPage }))
);
const ForgotPasswordPage = lazy(() =>
  import("@/pages/AuthRecoveryPages").then((m) => ({ default: m.ForgotPasswordPage }))
);
const ResetPasswordPage = lazy(() =>
  import("@/pages/AuthRecoveryPages").then((m) => ({ default: m.ResetPasswordPage }))
);
const ForgotIdentifierPage = lazy(() =>
  import("@/pages/AuthRecoveryPages").then((m) => ({ default: m.ForgotIdentifierPage }))
);

// ============================================================================
// SITE INSTITUTIONNEL — module autonome (préparé pour migration domaine dédié)
// ============================================================================
const SiteHome = lazy(() => import("@/site/pages/SiteHome"));
const EliaPresentation = lazy(() => import("@/site/pages/EliaPresentation"));
const DemoShowcase = lazy(() =>
  import("@/site/pages/DemoShowcase").then((m) => ({ default: m.DemoShowcase }))
);
const DemoDossierDetail = lazy(() =>
  import("@/site/pages/DemoShowcase").then((m) => ({ default: m.DemoDossierDetail }))
);
const ROISimulator = lazy(() => import("@/site/pages/ROISimulator"));
const Comparator = lazy(() => import("@/site/pages/Comparator"));
const DocsHub = lazy(() => import("@/site/pages/DocsHub"));
const ContactPage = lazy(() => import("@/site/pages/ContactPage"));

// Admin — Centres opérationnels (Phase C)
const ExecutiveDashboardPage = lazy(() => import("@/pages/admin/ExecutiveDashboardPage"));
const AdminPortalPage = lazy(() => import("@/pages/admin/AdminPortalPage"));
const AuditCenterPage = lazy(() => import("@/pages/admin/AuditCenterPage"));
const CommitteeRoomPage = lazy(() => import("@/pages/admin/CommitteeRoomPage"));
const SupportCenterPage = lazy(() => import("@/pages/admin/SupportCenterPage"));
const ConfigurationPage = lazy(() => import("@/pages/admin/ConfigurationPage"));

// Moi — Zone de saisie CRM (Personnel, Revenus, Dépenses, etc.)
const MoiLayout = lazy(() => import("@/pages/moi/MoiLayout"));
const MoiHome = lazy(() => import("@/pages/moi/MoiHome"));
const MoiSectionPage = lazy(() => import("@/pages/moi/MoiSectionPage"));

// Dossiers — Générateur automatique à partir du profil Moi
const DossiersCatalog = lazy(() => import("@/pages/dossiers/DossiersCatalog"));
const DossierGenerate = lazy(() => import("@/pages/dossiers/DossierGenerate"));
const DossierView = lazy(() => import("@/pages/dossiers/DossierView"));

// Smart Docs — Intelligence documentaire (OCR + classif + extract + expiration)
const SmartDocsHub = lazy(() => import("@/pages/smart-docs/SmartDocsHub"));

// Placeholders du menu Profil global
const NotificationsPage = lazy(() => import("@/pages/NotificationsPage"));
const BillingPage = lazy(() => import("@/pages/BillingPage"));

function AppRouter() {
  const location = useLocation();

  // Auth callback : détecter session_id AVANT toute route (évite race condition).
  if (location.hash?.includes("session_id=")) {
    return (
      <Suspense fallback={null}>
        <AuthCallback />
      </Suspense>
    );
  }

  return (
    <Suspense fallback={<div className="min-h-screen" />}>
      <BugReportOverlay />
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/partage/:token" element={<SharedView />} />

        {/* Réseau de partenaires — routes publiques adaptables au type */}
        <Route path="/partenaires" element={
          <ProtectedRoute>
            <AppShell>
              <PartnersPage />
            </AppShell>
          </ProtectedRoute>
        } />
        <Route path="/bank/:slug" element={<PartnerLandingPage kindHint="bank" />} />
        <Route path="/coop/:slug" element={<PartnerLandingPage kindHint="coop" />} />
        <Route path="/advisor/:slug" element={<PartnerLandingPage kindHint="advisor" />} />
        <Route path="/courtier/:slug" element={<PartnerLandingPage kindHint="courtier" />} />
        <Route path="/imf/:slug" element={<PartnerLandingPage kindHint="imf" />} />
        <Route path="/partner/:slug" element={<PartnerLandingPage kindHint="partner" />} />

        <Route
          path="/onboarding"
          element={
            <ProtectedRoute>
              <OnboardingPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/onboarding/conversation"
          element={
            <ProtectedRoute>
              <Onboarding />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <AppShell>
                <Dashboard />
              </AppShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/dossier"
          element={<Navigate to="/moi" replace />}
        />
        <Route
          path="/moi"
          element={
            <ProtectedRoute>
              <MoiLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<MoiHome />} />
          <Route path=":sectionKey" element={<MoiSectionPage />} />
        </Route>

        {/* Profil dynamique — sélection du type de dossier */}
        <Route
          path="/profile-type"
          element={
            <ProtectedRoute>
              <AppShell>
                <ProfileTemplatePage />
              </AppShell>
            </ProtectedRoute>
          }
        />

        {/* Dossiers — Générateur automatique */}
        <Route
          path="/dossiers"
          element={
            <ProtectedRoute>
              <AppShell>
                <DossiersCatalog />
              </AppShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/dossiers/generate/:productKey"
          element={
            <ProtectedRoute>
              <AppShell>
                <DossierGenerate />
              </AppShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/dossiers/view/:dossierId"
          element={
            <ProtectedRoute>
              <AppShell>
                <DossierView />
              </AppShell>
            </ProtectedRoute>
          }
        />

        {/* Smart Docs — OCR + classif + extraction */}
        <Route
          path="/smart-docs"
          element={
            <ProtectedRoute>
              <AppShell>
                <SmartDocsHub />
              </AppShell>
            </ProtectedRoute>
          }
        />

        {/* Notifications & Facturation (placeholders du menu Profil) */}
        <Route
          path="/notifications"
          element={
            <ProtectedRoute>
              <AppShell>
                <NotificationsPage />
              </AppShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/billing"
          element={
            <ProtectedRoute>
              <AppShell>
                <BillingPage />
              </AppShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/altiora"
          element={
            <ProtectedRoute>
              <AppShell>
                <AltioraPage />
              </AppShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/feature-flags"
          element={
            <ProtectedRoute>
              <AppShell>
                <FeatureFlagsAdmin />
              </AppShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/assistant"
          element={
            <ProtectedRoute>
              <AppShell>
                <Assistant />
              </AppShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/documents"
          element={
            <ProtectedRoute>
              <AppShell>
                <DocumentsPage />
              </AppShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/generation"
          element={
            <ProtectedRoute>
              <AppShell>
                <GenerationPage />
              </AppShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/geolocation"
          element={
            <ProtectedRoute>
              <AppShell>
                <GeolocationPage />
              </AppShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/institution"
          element={
            <ProtectedRoute>
              <AppShell>
                <InstitutionPage />
              </AppShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/hub"
          element={
            <ProtectedRoute>
              <AppShell>
                <PartnerHubPage />
              </AppShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/foliomap"
          element={
            <ProtectedRoute>
              <AppShell>
                <FolioMapInstitutionPage />
              </AppShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/timeline"
          element={
            <ProtectedRoute>
              <AppShell>
                <TimelinePage />
              </AppShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/copilot"
          element={
            <ProtectedRoute>
              <AppShell>
                <CopilotPage />
              </AppShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/agenda"
          element={
            <ProtectedRoute>
              <AppShell>
                <AgendaPage />
              </AppShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/opportunities"
          element={
            <ProtectedRoute>
              <AppShell>
                <OpportunitiesPage />
              </AppShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/plans"
          element={
            <ProtectedRoute>
              <AppShell>
                <PlansPage />
              </AppShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/security"
          element={
            <ProtectedRoute>
              <AppShell>
                <SecurityCenterPage />
              </AppShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/knowledge"
          element={
            <ProtectedRoute>
              <AppShell>
                <KnowledgeCenterPage />
              </AppShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/field"
          element={
            <ProtectedRoute>
              <AppShell>
                <FieldPage />
              </AppShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/collection"
          element={
            <ProtectedRoute>
              <AppShell>
                <CollectionPage />
              </AppShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/output"
          element={
            <ProtectedRoute>
              <AppShell>
                <OutputEnginePage />
              </AppShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/progression"
          element={
            <ProtectedRoute>
              <ProgressionPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/projects"
          element={
            <ProtectedRoute>
              <AppShell>
                <ProjectsPage />
              </AppShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/projects/:projectId/progression"
          element={
            <ProtectedRoute>
              <ProgressionPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/projects/:projectId/institution"
          element={
            <ProtectedRoute>
              <InstitutionPreview />
            </ProtectedRoute>
          }
        />
        <Route
          path="/bienvenue"
          element={
            <ProtectedRoute>
              <OnboardingWizard />
            </ProtectedRoute>
          }
        />
        <Route path="/payment/success" element={<PaymentSuccessPage />} />
        <Route path="/payment/cancel" element={<PaymentCancelPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/forgot-identifier" element={<ForgotIdentifierPage />} />

        {/* Portail public — sans auth */}
        <Route path="/portal" element={<PublicPortalPage />} />
        <Route path="/portal/link/:token" element={<PublicPortalPage />} />
        <Route path="/portal/:audience" element={<PublicPortalPage />} />
        <Route path="/portal/:audience/:slug" element={<PublicPortalPage />} />

        {/* Site institutionnel FolioMap/ELIA — public, sans auth */}
        <Route path="/site" element={<SiteHome />} />
        <Route path="/site/elia" element={<EliaPresentation />} />
        <Route path="/site/demo" element={<DemoShowcase />} />
        <Route path="/site/demo/:dossierId" element={<DemoDossierDetail />} />
        <Route path="/site/roi" element={<ROISimulator />} />
        <Route path="/site/compare" element={<Comparator />} />
        <Route path="/site/docs" element={<DocsHub />} />
        <Route path="/site/contact" element={<ContactPage />} />

        {/* Admin — Centres opérationnels (Phase C) — protégés */}
        <Route
          path="/admin/executive"
          element={
            <ProtectedRoute>
              <AppShell>
                <ExecutiveDashboardPage />
              </AppShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/portal"
          element={
            <ProtectedRoute>
              <AppShell>
                <AdminPortalPage />
              </AppShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/audit"
          element={
            <ProtectedRoute>
              <AppShell>
                <AuditCenterPage />
              </AppShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/committee"
          element={
            <ProtectedRoute>
              <AppShell>
                <CommitteeRoomPage />
              </AppShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/support"
          element={
            <ProtectedRoute>
              <AppShell>
                <SupportCenterPage />
              </AppShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/config"
          element={
            <ProtectedRoute>
              <AppShell>
                <ConfigurationPage />
              </AppShell>
            </ProtectedRoute>
          }
        />
      </Routes>
    </Suspense>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <FeatureFlagsProvider>
          <AppRouter />
          <Toaster richColors position="top-right" />
        </FeatureFlagsProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
